import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { InviteGuestUsersToOrganization, GetLanguages, GetOrganization } from 'Apollo';
import FormUsersCreationSkeleton from './FormUsersCreation.skeleton';
import FormUsersCreationSuccess from './FormUsersCreation.success';
import FormGuestUsersInvitation from './FormGuestUsersInvitation';
import { TUsersCreation } from './FormUsersCreation.container';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { setErrorMessage } from 'Tools/setErrorMessage';

type Props = {
  onCancel(): void;
  organizationCode: string;
};

const FormGuestUsersInvitationContainer = (props: Props) => {
  const { organizationCode } = props;
  const ts = useTranslate();
  const { onError } = useNotification();

  const { data: languages, loading: loadingLanguage } = useQuery<
    GetLanguagesData
  >(GetLanguages);

  const { data: organization, loading: loadingOrganization } = useQuery<
    GetOrganizationData,
    GetOrganizationVars
  >(GetOrganization, { variables: { code: organizationCode } });

  const [
    inviteGuestUsers,
    { data: invitedGuestUsers, loading: loadingInvitation }
  ] = useMutation<
    InviteGuestUsersToOrganizationData,
    InviteGuestUsersToOrganizationVariables
  >(InviteGuestUsersToOrganization);

  const onSubmit = ({ emails, language }: TUsersCreation) => {
    inviteGuestUsers({
      variables: {
        organizationCode,
        userEmails: emails,
        invitationLanguage: language
      },
      onError: (e: ApolloError) => {
        onError(setErrorMessage(e, 'HC0045', ts('error.type.HC0045A'), 'X'));
      },
      refetchQueries: ['GetOrganizationGuestUsers']
    });
  };

  if (loadingInvitation || loadingLanguage || loadingOrganization)
    return <FormUsersCreationSkeleton />;

  if (invitedGuestUsers) {
    const invitedPersonsArray =
      invitedGuestUsers.inviteGuestUsersToOrganization;
    const invitationErrors = invitedPersonsArray.filter(inv => !!inv.error);
    const invitationsDelivered =
      invitedPersonsArray.length - invitationErrors.length;

    return (
      <FormUsersCreationSuccess
        failuredInvitations={invitationErrors}
        invitationsQuantity={invitationsDelivered}
        onClose={props.onCancel}
      />
    );
  }

  const languagesList =
    languages?.languages.edges.map(({ node }) => node) ?? [];

  return (
    <FormGuestUsersInvitation
      onCancel={props.onCancel}
      onSubmit={onSubmit}
      languages={languagesList}
      loading={loadingInvitation}
      organizationName={organization?.organization.name}
    />
  );
};

export default FormGuestUsersInvitationContainer;
